import { InjectionToken } from '@angular/core';

import type { Observable } from 'rxjs';

/**
 * The ONLY feature flags the frontend reads — a closed union of app-known,
 * semantic aliases. Adding a flag = one tuple line here + one map line in
 * {@link FEATURE_FLAG_MAP}. Only flags with a real frontend consumer belong here.
 *
 * Backend-agnostic by design: the dirty (French) backend `featureName` strings
 * live in the core-layer {@link FEATURE_FLAG_MAP}, never in this shared contract
 * (mirrors how `SectionRight` aliases stay in @shared while the `USERRIGHT_*`
 * codes stay in core/authorization).
 */
export const KNOWN_FEATURE_FLAGS = [
  'OrderCategoryFilter', // Commandes_FiltreCategorie
  'EventDuplication', // Evenements_Duplication
  'PlanControlStatusFilter', // PlanDuCatalogue_FiltreStatutControle
  'OrdersExportLimit', // Commandes_Export_Limite3000
] as const;

export type FeatureFlag = (typeof KNOWN_FEATURE_FLAGS)[number];

export function isFeatureFlag(value: unknown): value is FeatureFlag {
  return KNOWN_FEATURE_FLAGS.includes(value as FeatureFlag);
}

/**
 * Inbound contract consumers depend on. Fail-closed everywhere: an unknown or
 * absent flag, or any fetch failure, resolves to disabled.
 */
export abstract class IFeatureFlagService {
  /** Sync, reactive, fail-closed: `false` unless the flag set is ready and grants it. */
  abstract isEnabled(flag: FeatureFlag): boolean;
  /** Guard-facing: awaits the first non-pending resolution, then decides once. */
  abstract isEnabledWhenReady(flag: FeatureFlag): Observable<boolean>;
}

export const FEATURE_FLAG_SERVICE = new InjectionToken<IFeatureFlagService>('FeatureFlagService');

/**
 * Outbound port: fetches and resolves the raw backend flag set into the closed
 * alias set. Implemented by the HTTP and Mock adapters in core/feature-flags.
 */
export abstract class IFeatureFlagApiService {
  abstract getFeatureFlags(): Observable<ReadonlySet<FeatureFlag>>;
}

export const FEATURE_FLAG_API_SERVICE = new InjectionToken<IFeatureFlagApiService>('FeatureFlagApiService');
